/**
 * Logger Utility
 * 
 * Centralized logging utility that can be easily configured to:
 * - Disable logging in production
 * - Send logs to external services
 * - Format logs consistently
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  /** Whether logging is enabled (default: true in development, false in production) */
  enabled: boolean;
  /** Minimum log level to output */
  minLevel: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const isDevelopment = process.env.NODE_ENV === 'development';

const config: LoggerConfig = {
  enabled: isDevelopment,
  minLevel: 'debug',
};

const shouldLog = (level: LogLevel): boolean => {
  return config.enabled && LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
};

/**
 * Logger object with methods for different log levels
 */
export const logger = {
  debug: (...args: unknown[]): void => {
    if (shouldLog('debug')) {
      console.debug('[DEBUG]', ...args);
    }
  },

  info: (...args: unknown[]): void => {
    if (shouldLog('info')) {
      console.info('[INFO]', ...args);
    }
  },

  warn: (...args: unknown[]): void => {
    if (shouldLog('warn')) {
      console.warn('[WARN]', ...args);
    }
  },

  error: (...args: unknown[]): void => {
    if (shouldLog('error')) {
      console.error('[ERROR]', ...args);
    }
  },
};

export default logger;

